import { get } from 'svelte/store';
import { format } from 'svelte-i18n';

/**
 * Action contexts for shared-home "managed by the home owner" messaging.
 * Each context maps to a slightly different explanation so the copy reflects
 * the specific action the user attempted (FE-2007).
 */
export type SharedHomeManagedContext =
	| 'generic'
	| 'add_device'
	| 'add_room'
	| 'add_group'
	| 'add_scene'
	| 'add_alarm'
	| 'add_thermostat'
	| 'edit_entity'
	| 'delete_entity'
	| 'transfer_device'
	| 'share_device'
	| 'change_view';

export interface SharedHomeManagedCopy {
	headingKey: string;
	headingDefault: string;
	messageKey: string;
	messageDefault: string;
}

const HEADING_KEY = 'shared_home_managed_by_owner';
const HEADING_DEFAULT = 'Managed by the home owner.';

/**
 * Per-context explanation text. The heading stays constant; only the message
 * changes so the user understands which capability is owner-managed.
 */
const CONTEXT_MESSAGE: Record<SharedHomeManagedContext, { key: string; default: string }> = {
	generic: {
		key: 'shared_home_managed_by_owner_desc',
		default:
			'This home is shared with you. This action is managed by the home owner. Contact the home owner or sign in with the owner account to continue.',
	},
	add_device: {
		key: 'shared_home_managed_by_owner_add_device_desc',
		default:
			'This home is shared with you. Adding devices is managed by the home owner. Contact the home owner or sign in with the owner account to continue.',
	},
	add_room: {
		key: 'shared_home_managed_by_owner_add_room_desc',
		default:
			'This home is shared with you. Adding rooms is managed by the home owner. Contact the home owner or sign in with the owner account to continue.',
	},
	add_group: {
		key: 'shared_home_managed_by_owner_add_group_desc',
		default:
			'This home is shared with you. Adding groups is managed by the home owner. Contact the home owner or sign in with the owner account to continue.',
	},
	add_scene: {
		key: 'shared_home_managed_by_owner_add_scene_desc',
		default:
			'This home is shared with you. Adding scenes is managed by the home owner. Contact the home owner or sign in with the owner account to continue.',
	},
	add_alarm: {
		key: 'shared_home_managed_by_owner_add_alarm_desc',
		default:
			'This home is shared with you. Adding alarms is managed by the home owner. Contact the home owner or sign in with the owner account to continue.',
	},
	add_thermostat: {
		key: 'shared_home_managed_by_owner_add_thermostat_desc',
		default:
			'This home is shared with you. Adding thermostats is managed by the home owner. Contact the home owner or sign in with the owner account to continue.',
	},
	edit_entity: {
		key: 'shared_home_managed_by_owner_edit_desc',
		default:
			'This home is shared with you. Editing is managed by the home owner. Contact the home owner or sign in with the owner account to continue.',
	},
	delete_entity: {
		key: 'shared_home_managed_by_owner_delete_desc',
		default:
			'This home is shared with you. Removing is managed by the home owner. Contact the home owner or sign in with the owner account to continue.',
	},
	transfer_device: {
		key: 'shared_home_managed_by_owner_transfer_desc',
		default:
			'This home is shared with you. Transferring devices is managed by the home owner. Contact the home owner or sign in with the owner account to continue.',
	},
	share_device: {
		key: 'shared_home_managed_by_owner_share_desc',
		default:
			'This home is shared with you. Sharing devices is managed by the home owner. Contact the home owner or sign in with the owner account to continue.',
	},
	change_view: {
		key: 'shared_home_managed_by_owner_change_view_desc',
		default:
			'This home is shared with you. Changing device views is managed by the home owner. Contact the home owner or sign in with the owner account to continue.',
	},
};

/**
 * Returns the i18n key/default pairs for the shared-home owner-managed copy of
 * a given action context. Components render these with `$format`.
 */
export const getSharedHomeManagedCopy = (
	context: SharedHomeManagedContext = 'generic',
): SharedHomeManagedCopy => {
	const message = CONTEXT_MESSAGE[context] ?? CONTEXT_MESSAGE.generic;
	return {
		headingKey: HEADING_KEY,
		headingDefault: HEADING_DEFAULT,
		messageKey: message.key,
		messageDefault: message.default,
	};
};

/**
 * Maps an add-action menu key (see `dropdownSubmenuData` in `main_menu.ts`) to
 * the matching shared-home messaging context for the intercept modal.
 */
export const getSharedHomeManagedContextForAddActionKey = (key: string): SharedHomeManagedContext => {
	switch (key) {
		case 'devices':
			return 'add_device';
		case 'rooms':
			return 'add_room';
		case 'groups':
			return 'add_group';
		case 'scenes':
			return 'add_scene';
		case 'alarms':
			return 'add_alarm';
		case 'thermostat':
			return 'add_thermostat';
		default:
			return 'generic';
	}
};

/**
 * Returns the translated explanation string for use as a tooltip title on
 * disabled icon-only controls.
 */
export const getSharedHomeManagedTooltip = (context: SharedHomeManagedContext = 'generic'): string => {
	const { messageKey, messageDefault } = getSharedHomeManagedCopy(context);
	return get(format)(messageKey, { default: messageDefault });
};

/**
 * Entities that render an empty-list placeholder and, on shared homes, need
 * owner-managed explanatory copy instead of the default "try creating…" text
 * (FE-2011).
 */
export type SharedHomeEmptyListEntity =
	'rooms' | 'groups' | 'scenes' | 'alarms' | 'thermostats' | 'devices';

export interface SharedHomeEmptyListCopy {
	titleKey: string;
	titleDefault: string;
	descrKey: string;
	descrDefault: string;
}

const EMPTY_LIST_TITLE_KEY = 'shared_home_empty_list_title';
const EMPTY_LIST_TITLE_DEFAULT = 'Nothing here yet';

/**
 * Per-entity description shown on shared homes when a list is empty. Each entity
 * gets its own full-sentence string (rather than a parameterized `{type}`) so
 * translators can adapt word order and grammar per language.
 */
const EMPTY_LIST_DESCR: Record<SharedHomeEmptyListEntity, { key: string; default: string }> = {
	rooms: {
		key: 'shared_home_empty_rooms_descr',
		default:
			'This home is shared with you. Rooms are managed by the home owner. Contact the home owner if you need changes.',
	},
	groups: {
		key: 'shared_home_empty_groups_descr',
		default:
			'This home is shared with you. Groups are managed by the home owner. Contact the home owner if you need changes.',
	},
	scenes: {
		key: 'shared_home_empty_scenes_descr',
		default:
			'This home is shared with you. Scenes are managed by the home owner. Contact the home owner if you need changes.',
	},
	alarms: {
		key: 'shared_home_empty_alarms_descr',
		default:
			'This home is shared with you. Alarms are managed by the home owner. Contact the home owner if you need changes.',
	},
	thermostats: {
		key: 'shared_home_empty_thermostats_descr',
		default:
			'This home is shared with you. Thermostats are managed by the home owner. Contact the home owner if you need changes.',
	},
	devices: {
		key: 'shared_home_empty_devices_descr',
		default:
			'This home is shared with you. Devices are managed by the home owner. Contact the home owner if you need changes.',
	},
};

/**
 * Returns the i18n key/default pairs for the shared-home empty-list copy of a
 * given entity. Components render these with `$format` and should only use them
 * when {@link isActiveHomeShared} is true and the list is empty.
 */
export const getSharedHomeEmptyListCopy = (
	entity: SharedHomeEmptyListEntity,
): SharedHomeEmptyListCopy => {
	const descr = EMPTY_LIST_DESCR[entity] ?? EMPTY_LIST_DESCR.devices;
	return {
		titleKey: EMPTY_LIST_TITLE_KEY,
		titleDefault: EMPTY_LIST_TITLE_DEFAULT,
		descrKey: descr.key,
		descrDefault: descr.default,
	};
};
